import React, { useState, useEffect } from 'react';
import { X, Upload, Trash2, Plus } from 'lucide-react';
import { api } from '../../api';
import toast from 'react-hot-toast';
import useFileUpload from '../../hooks/useFileUpload';
import { categoryLabel } from '../../utils/courseSubcategoryNodes';

/**
 * CourseCreationModal - Sprint 2 Epic 02 Story 01
 * Modal for creating new courses or editing existing ones
 *
 * `categories` (names) lets the admin Courses page offer the categories added
 * with "Add Category"; without it the four built-in categories are listed.
 * `initialCategory` pre-selects a category for a new course (sidebar "Add content").
 * `subcategoryNodes(category)` (admin Courses pages) lists the category's subcategories; the form
 * then asks for one first, then optionally one of its children and so on (Action Songs > Vocal
 * Songs); a course left at Action Songs goes there.
 * `initialSubcategory` pre-selects one. `initialTaskType` sets the task type (a task type
 * row's "+ Add content", e.g. English > Poem); the course is then listed there by its task type,
 * so the subcategory is optional. There is no task type field: a course gets its task type only
 * that way, and an edited course keeps the one it has.
 */

// Task type rows can't hold a course by subcategory (they list courses by task type).
const placeable = (nodes) =>
  nodes.filter((node) => node.kind !== 'taskType').map((node) => ({ ...node, children: placeable(node.children || []) }));

// The nodes from the top down to `key` ([] when it isn't there).
const chainTo = (nodes, key) => {
  for (const node of nodes) {
    if (node.key === key) return [node];
    const rest = chainTo(node.children, key);
    if (rest.length) return [node, ...rest];
  }
  return [];
};

const DEFAULT_CATEGORIES = ['Computer Apps', 'Art', 'Spoken English', 'Life Skills'];

export default function CourseCreationModal({
  isOpen,
  onClose,
  onCourseCreated,
  courseToEdit = null,
  // Admin-managed category names (Courses page); falls back to the original fixed list.
  categories = null,
  initialCategory = '',
  subcategoryNodes = null,
  initialSubcategory = '',
  initialTaskType = ''
}) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: initialCategory || '',
    subcategory: initialSubcategory || '',
    difficultyLevel: '',
    icon: '📚',
    taskType: initialTaskType || '',
    // Shown once a task type is chosen: the course's total coins and the
    // parameters (name + coins) that share them without going over.
    totalCoins: '',
    coinParameters: [],
    thumbnail: ''
  });

  const [errors, setErrors] = useState({});
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  // Picked or removed here. Editing sends the thumbnail only then (see handleSubmit).
  const [thumbnailChanged, setThumbnailChanged] = useState(false);
  const [loading, setLoading] = useState(false);

  const { uploadFile } = useFileUpload();

  const isEditMode = !!courseToEdit;

  // Populate form if editing
  useEffect(() => {
    if (courseToEdit) {
      setFormData({
        title: courseToEdit.title || '',
        description: courseToEdit.description || '',
        category: courseToEdit.category || '',
        subcategory: courseToEdit.subcategory || '',
        difficultyLevel: courseToEdit.difficultyLevel || '',
        icon: courseToEdit.icon || '📚',
        taskType: courseToEdit.taskType || '',
        totalCoins: courseToEdit.totalCoins ? String(courseToEdit.totalCoins) : '',
        coinParameters: (courseToEdit.coinParameters || []).map((parameter) => ({
          label: parameter.label || '',
          coins: String(parameter.coins ?? '')
        })),
        thumbnail: courseToEdit.thumbnail || ''
      });

      if (courseToEdit.thumbnail) {
        setThumbnailPreview(courseToEdit.thumbnail);
      }
    }
  }, [courseToEdit]);

  const handleChange = (field, value) => {
    // Another category has other subcategories.
    setFormData((prev) => ({ ...prev, [field]: value, ...(field === 'category' ? { subcategory: '' } : {}) }));

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // One select per level: the subcategory, then one of its children, and so on down the picked path.
  const subcategoryOptions = subcategoryNodes ? placeable(subcategoryNodes(formData.category)) : [];
  const subcategoryChain = chainTo(subcategoryOptions, formData.subcategory);
  const subcategoryLevels = [];
  for (let level = 0, options = subcategoryOptions; options.length; level += 1) {
    subcategoryLevels.push({ options, above: subcategoryChain[level - 1] || null, value: subcategoryChain[level]?.key || '' });
    if (!subcategoryChain[level]) break;
    options = subcategoryChain[level].children;
  }
  const subcategoryLast = subcategoryChain[subcategoryChain.length - 1];
  // Opened from a task type row and still that task type: the course is listed by its task type.
  const placedByTaskType = Boolean(initialTaskType) && formData.taskType === initialTaskType;
  const subcategoryRequired = !isEditMode && !placedByTaskType;

  // Coin parameters: add a row, change one field of a row, or remove a row.
  const setCoinParameters = (update) => {
    setFormData((prev) => ({ ...prev, coinParameters: update(prev.coinParameters) }));
    if (errors.coinParameters) {
      setErrors((prev) => ({ ...prev, coinParameters: '' }));
    }
  };
  const addCoinParameter = () => setCoinParameters((list) => [...list, { label: '', coins: '' }]);
  const changeCoinParameter = (index, field, value) =>
    setCoinParameters((list) => list.map((parameter, i) => (i === index ? { ...parameter, [field]: value } : parameter)));
  const removeCoinParameter = (index) => setCoinParameters((list) => list.filter((_, i) => i !== index));

  const totalCoins = Number(formData.totalCoins) || 0;
  const usedCoins = formData.coinParameters.reduce((sum, parameter) => sum + (Number(parameter.coins) || 0), 0);
  const overCoinLimit = usedCoins > totalCoins;

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({
        ...prev,
        thumbnail: 'Please select an image file (JPG or PNG)'
      }));
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        thumbnail: 'Image size must be less than 2MB'
      }));
      return;
    }

    setThumbnailFile(file);
    setThumbnailChanged(true);

    // Show preview immediately using base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setThumbnailPreview(base64);
      // Also store base64 as fallback thumbnail in case S3 upload fails later
      setFormData((prev) => ({ ...prev, thumbnail: base64 }));
    };
    reader.readAsDataURL(file);

    // Clear error
    setErrors((prev) => ({ ...prev, thumbnail: '' }));
    toast.success('Thumbnail selected — it will be uploaded when you save');
  };

  const removeThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setThumbnailChanged(true);
    setFormData((prev) => ({ ...prev, thumbnail: '' }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Course title is required';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be 100 characters or less';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    if (!formData.category) {
      newErrors.category = 'Please select a category';
    }

    // New courses must name their subcategory (Action Songs); its children (Vocal Songs) are
    // optional, and without one the course goes into the row above. Older courses being edited
    // may have none yet.
    if (subcategoryRequired && subcategoryOptions.length && !subcategoryLast) {
      newErrors.subcategory = 'Please select a subcategory';
    }

    if (!formData.difficultyLevel) {
      newErrors.difficultyLevel = 'Please select a difficulty level';
    }

    if (formData.taskType) {
      const total = Number(formData.totalCoins);
      if (String(formData.totalCoins).trim() === '') {
        newErrors.totalCoins = 'Please enter the total coins';
      } else if (!Number.isInteger(total) || total < 1) {
        newErrors.totalCoins = 'Total coins must be a whole number of 1 or more';
      }

      const names = new Set();
      for (const parameter of formData.coinParameters) {
        const name = parameter.label.trim();
        const coins = Number(parameter.coins);
        if (!name) {
          newErrors.coinParameters = 'Every parameter needs a name';
        } else if (names.has(name.toLowerCase())) {
          newErrors.coinParameters = `"${name}" is listed twice`;
        } else if (String(parameter.coins).trim() === '' || !Number.isInteger(coins) || coins < 0) {
          newErrors.coinParameters = `Enter the coins for "${name}" (a whole number of 0 or more)`;
        }
        if (newErrors.coinParameters) break;
        names.add(name.toLowerCase());
      }
      if (!newErrors.coinParameters && !newErrors.totalCoins && overCoinLimit) {
        newErrors.coinParameters = `The parameters use ${usedCoins} coins, more than the total of ${total}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    try {
      setLoading(true);

      // The form sends a picked thumbnail as base64; the server uploads it to the storage bucket and
      // saves only its link (backend services/courseThumbnailStorage.js), failing the save if it can't.
      const shouldUploadThumbnailToS3 = false;
      let thumbnailUrl = formData.thumbnail;
      if (shouldUploadThumbnailToS3 && thumbnailFile) {
        try {
          const uploadResult = await uploadFile({
            file: thumbnailFile,
            fileType: 'image',
            id: `course-thumbnail-${Date.now()}`
          });

          if (uploadResult.success) {
            thumbnailUrl = uploadResult.cdnUrl;
          } else {
            // S3 upload failed — use base64 thumbnail as fallback so course can still be saved
            console.warn('Thumbnail S3 upload failed, using base64 fallback:', uploadResult.error);
            toast('Thumbnail saved locally — S3 upload can be retried later', { icon: '⚠️', duration: 4000 });
            // thumbnailUrl already has base64 from handleThumbnailChange
          }
        } catch (uploadError) {
          console.error('Thumbnail upload exception:', uploadError);
          toast('Thumbnail saved locally — S3 upload can be retried later', { icon: '⚠️', duration: 4000 });
          // thumbnailUrl already has base64 from handleThumbnailChange
        }
      }

      const courseData = {
        ...formData,
        // A new course only goes into a subcategory listed for its category; an edited one keeps
        // whatever it had unless another one was picked.
        subcategory: subcategoryChain.length || isEditMode ? formData.subcategory : '',
        // Without a task type there is no coin plan (the API clears it too).
        totalCoins: formData.taskType ? Number(formData.totalCoins) || 0 : 0,
        coinParameters: formData.taskType
          ? formData.coinParameters.map((parameter) => ({
            label: parameter.label.trim(),
            coins: Number(parameter.coins) || 0
          }))
          : [],
        thumbnail: thumbnailUrl
      };
      // Editing leaves the stored thumbnail as it is unless one was picked or removed here: the
      // course lists come without thumbnails, so this form may not have it, and resending an
      // unchanged one (MBs as base64) changes nothing.
      if (isEditMode && !thumbnailChanged) delete courseData.thumbnail;

      let response;
      if (isEditMode) {
        // Update existing course
        response = await api.put(`/api/v2/lms/admin/courses/${courseToEdit._id}`, courseData);
      } else {
        // Create new course
        response = await api.post('/api/v2/lms/admin/courses', courseData);
      }

      if (response.data.success) {
        onCourseCreated(response.data.data);
        onClose();
      }
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error(
        error.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} course`
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="mt-14 bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-purple-50 border-b border-purple-100 px-6 py-4 flex flex-shrink-0 items-center justify-between">
          <h2 className="text-2xl font-bold text-purple-900">
            {isEditMode ? 'Edit Course Metadata' : 'Create New Course'}
          </h2>
          <button
            onClick={onClose}
            className="text-purple-400 hover:text-purple-600 transition-colors"
            disabled={loading}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-scroll custom-scrollbar p-6 space-y-6">
          {/* Subcategory first (Action Songs), then one of its children (Vocal Songs), and so on */}
          {subcategoryOptions.length > 0 && (
            <div>
              <div className="grid grid-cols-2 gap-4">
                {subcategoryLevels.map(({ options, above, value }, level) => {
                  const id = level ? `course-subcategory-${level}` : 'course-subcategory';
                  // Only the first one is required; a child left empty keeps the course in the row above.
                  const required = subcategoryRequired && !level;
                  return (
                    <div key={above?.key || 'top'}>
                      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
                        {above ? above.label : 'Subcategory'}{' '}
                        {required ? <span className="text-red-500">*</span> : !isEditMode && <span className="text-gray-400">(Optional)</span>}
                      </label>
                      <select
                        id={id}
                        value={value}
                        onChange={(e) => handleChange('subcategory', e.target.value || above?.key || '')}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors.subcategory ? 'border-red-500' : 'border-gray-300'
                          }`}
                      >
                        <option value="">{above ? `None (add to ${above.label})` : 'Select subcategory'}</option>
                        {options.map((node) => (
                          <option key={node.key} value={node.key}>{node.icon} {node.label}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
              {errors.subcategory && (
                <p className="text-sm text-red-600 mt-1">{errors.subcategory}</p>
              )}
            </div>
          )}

          {/* Course Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Course Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="e.g., Advanced Computer Apps"
              maxLength={100}
            />
            {errors.title && (
              <p className="text-sm text-red-600 mt-1">{errors.title}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {formData.title.length}/100 characters
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 resize-none ${errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
              rows={4}
              placeholder="Describe the course content and learning objectives..."
              maxLength={500}
            />
            {errors.description && (
              <p className="text-sm text-red-600 mt-1">{errors.description}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {formData.description.length}/500 characters
            </p>
          </div>

          {/* Category & Difficulty (2-column layout) */}
          <div className="grid grid-cols-2 gap-4">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors.category ? 'border-red-500' : 'border-gray-300'
                  }`}
              >
                <option value="">Select category</option>
                {[...new Set([
                  ...(categories && categories.length ? categories : ['Computer Apps', 'Art', 'Spoken English', 'Life Skills']),
                  ...(formData.category ? [formData.category] : []),
                ])].map((name) => (
                  <option key={name} value={name}>{categoryLabel(name)}</option>
                ))}
              </select>
              {errors.category && (
                <p className="text-sm text-red-600 mt-1">{errors.category}</p>
              )}
            </div>

            {/* Difficulty Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Difficulty Level <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {['Beginner', 'Intermediate', 'Advanced'].map((level) => (
                  <label
                    key={level}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="difficultyLevel"
                      value={level}
                      checked={formData.difficultyLevel === level}
                      onChange={(e) =>
                        handleChange('difficultyLevel', e.target.value)
                      }
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">{level}</span>
                  </label>
                ))}
              </div>
              {errors.difficultyLevel && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.difficultyLevel}
                </p>
              )}
            </div>
          </div>

          {/* Coins for the task type: the total, then parameters that share it */}
          {formData.taskType && (
            <div className="rounded-lg border border-purple-100 bg-purple-50 p-4 space-y-4">
              <div>
                <label htmlFor="course-total-coins" className="block text-sm font-medium text-gray-700 mb-1">
                  Total Coins <span className="text-red-500">*</span>
                </label>
                <input
                  id="course-total-coins"
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={formData.totalCoins}
                  onChange={(e) => handleChange('totalCoins', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-purple-500 ${errors.totalCoins ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="e.g., 50"
                />
                {errors.totalCoins && (
                  <p className="text-sm text-red-600 mt-1">{errors.totalCoins}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  The most ISF coins for this course. The parameters below share it.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="text-sm font-medium text-gray-700">Parameters</span>
                  <span
                    className={`text-xs font-semibold ${overCoinLimit ? 'text-red-600' : 'text-gray-500'}`}
                    aria-live="polite"
                  >
                    {usedCoins} of {totalCoins} coins used
                    {!overCoinLimit && totalCoins > 0 ? ` · ${totalCoins - usedCoins} left` : ''}
                  </span>
                </div>

                {formData.coinParameters.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {formData.coinParameters.map((parameter, index) => (
                      // Grid, not flex: a global stylesheet (UserForm.css) gives every
                      // number input width:100%, which let the coins box squeeze the name.
                      // Name 75%, coins 25%, then the remove button.
                      <div key={index} className="grid grid-cols-[minmax(0,3fr)_minmax(0,1fr)_auto] items-center gap-2">
                        <input
                          type="text"
                          value={parameter.label}
                          onChange={(e) => changeCoinParameter(index, 'label', e.target.value)}
                          aria-label={`Parameter ${index + 1} name`}
                          placeholder="e.g., Expression"
                          maxLength={60}
                          className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-500"
                        />
                        <input
                          type="number"
                          min="0"
                          step="1"
                          inputMode="numeric"
                          value={parameter.coins}
                          onChange={(e) => changeCoinParameter(index, 'coins', e.target.value)}
                          aria-label={`Parameter ${index + 1} coins`}
                          placeholder="Coins"
                          className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-500"
                        />
                        <button
                          type="button"
                          onClick={() => removeCoinParameter(index)}
                          aria-label={`Remove parameter ${index + 1}`}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={addCoinParameter}
                  className="inline-flex items-center gap-1 px-4 py-2 border border-purple-300 text-purple-700 bg-white rounded-lg hover:bg-purple-100"
                >
                  <Plus size={16} /> Add Parameter
                </button>

                {(errors.coinParameters || overCoinLimit) && (
                  <p className="text-sm text-red-600 mt-2">
                    {errors.coinParameters || `The parameters use ${usedCoins} coins, more than the total of ${totalCoins}`}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Thumbnail Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Course Thumbnail <span className="text-red-500 font-bold">* (Required for publishing)</span>
            </label>

            {!thumbnailPreview ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-400 transition-colors">
                <Upload className="mx-auto mb-2 text-gray-400" size={32} />
                <p className="text-gray-600 mb-1">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Recommended: 1280x720px, JPG/PNG, max 2MB
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  className="hidden"
                  id="thumbnail-upload"
                />
                <label
                  htmlFor="thumbnail-upload"
                  className="inline-block px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer"
                >
                  Choose File
                </label>
              </div>
            ) : (
              <div className="relative">
                <img
                  src={thumbnailPreview}
                  alt="Thumbnail preview"
                  className="w-full h-48 object-cover rounded-lg"
                  onError={removeThumbnail}
                />
                <button
                  type="button"
                  onClick={removeThumbnail}
                  className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            )}

            {errors.thumbnail && (
              <p className="text-sm text-red-600 mt-1">{errors.thumbnail}</p>
            )}
          </div>

          {/* Info Banner */}
          {!isEditMode && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                ⚠️ Course will be created in <strong>Draft</strong> status. You
                can add content and publish it later.
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex-shrink-0 bg-white border-t border-gray-200 -mx-6 -mb-6 px-6 py-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading
                ? isEditMode
                  ? 'Updating...'
                  : 'Creating...'
                : isEditMode
                  ? 'Update Course'
                  : 'Create Course as Draft'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
